import { BillingDto, StatoAvanzamentoLavori } from '@api/client';
import dayjs from 'dayjs';
import { ChangeEvent } from 'react';

/**
 * Le righe SAL non ancora salvate su BE non hanno (ancora) un `id` numerico reale.
 * `handleAddChild` (sotto) popola questo campo con un id temporaneo client-side
 * (`temp-<counter>`), cosi' da poter identificare la riga anche prima del salvataggio.
 * `isEmptyPlaceholder` marca la riga sintetica inserita da `useGetBillingData.ts` quando
 * una fattura non ha ancora nessun SAL: prima di questa feature una fattura del genere
 * mostrava comunque 1 riga (vuota, senza bottone elimina, perche' il bottone elimina non
 * esisteva). Il flag serve a distinguere questo caso da una riga figlia bozza aggiunta
 * volontariamente dall'utente (che invece resta eliminabile).
 * Definito localmente per non toccare il modello generato `StatoAvanzamentoLavori`.
 */
export type EditableStatoAvanzamentoLavori = StatoAvanzamentoLavori & {
  tempId?: string;
  isEmptyPlaceholder?: boolean;
};

const isSameSalRow = (sal: EditableStatoAvanzamentoLavori, salRowKey: number | string): boolean =>
  typeof salRowKey === 'number' ? sal.id === salRowKey : sal.tempId === salRowKey;

/**
 * Contatore in module scope usato per generare id temporanei client-side univoci per le
 * righe SAL non ancora salvate. Preferito a `Date.now()` (che puo' collidere se
 * `handleAddChild` viene invocato piu' volte nello stesso millisecondo, es. in test o click
 * ravvicinati) e a `crypto.randomUUID()` (non garantito in tutti i target/lib TS configurati
 * per questo progetto). Un semplice contatore incrementale garantisce unicita' per l'intera
 * sessione del browser senza dipendenze aggiuntive.
 */
let tempSalIdCounter = 0;
export const generateTempSalId = (): string => `temp-${++tempSalIdCounter}`;

/**
 * Aggiorna, all'interno di una specifica milestone (`billingId`), un solo elemento della
 * lista `statoAvanzamentoLavoriList`, identificato da `salRowKey` (id numerico per righe
 * già salvate, id temporaneo stringa per righe non ancora salvate). Le altre righe della
 * stessa milestone e le altre milestone non vengono toccate.
 */
const updateSalRow =
  (billingId: string, salRowKey: number | string, updater: (sal: EditableStatoAvanzamentoLavori) => EditableStatoAvanzamentoLavori) =>
  (prev: BillingDto[] | undefined): BillingDto[] | undefined =>
    prev?.map((item) => {
      if (item.id !== billingId) {
        return item;
      }
      return {
        ...item,
        statoAvanzamentoLavoriList: item.statoAvanzamentoLavoriList?.map((sal) =>
          isSameSalRow(sal, salRowKey) ? updater(sal) : sal
        ),
      };
    });

const useEditBill = (
  setBillingPlan: React.Dispatch<React.SetStateAction<BillingDto[] | undefined>>,
  setIsBillingDirty: React.Dispatch<React.SetStateAction<boolean>>
) => {
  const handleChangeCheckbox = (
    e: ChangeEvent<HTMLInputElement>,
    billingId: string,
    salRowKey: number | string
  ) => {
    setIsBillingDirty(true);
    setBillingPlan(
      updateSalRow(billingId, salRowKey, (sal) => ({
        ...sal,
        isInviato: e.target.checked,
      }))
    );
  };

  const handleChangeDate = (date: dayjs.Dayjs, billingId: string, salRowKey: number | string) => {
    setIsBillingDirty(true);
    setBillingPlan(
      updateSalRow(billingId, salRowKey, (sal) => ({
        ...sal,
        dataDiInvio: date?.add(12, 'hour').toDate() || undefined,
      }))
    );
  };

  const handleSelectChange = (e: string, billingId: string, salRowKey: number | string) => {
    setIsBillingDirty(true);
    setBillingPlan(
      updateSalRow(billingId, salRowKey, (sal) => ({
        ...sal,
        idTipologiaBlocco: {
          ...sal.idTipologiaBlocco,
          value: e,
        },
      }))
    );
  };

  const handleChangeImporto = (e: any, billingId: string, salRowKey: number | string) => {
    setIsBillingDirty(true);
    setBillingPlan(
      updateSalRow(billingId, salRowKey, (sal) => ({
        ...sal,
        importo: e,
      }))
    );
  };

  /**
   * Aggiunge una riga SAL "figlia" (bozza, solo client-side) in coda al gruppo
   * `statoAvanzamentoLavoriList` della milestone `billingId`. La riga figlia condivide
   * automaticamente i campi name/stage/order/amount/date perche' vivono su `BillingDto`
   * (la milestone), non sul singolo record SAL: qui basta appendere un nuovo elemento
   * "vuoto" alla lista di quella milestone. Il modello `StatoAvanzamentoLavori` non
   * espone altri campi condivisi a livello di milestone (es. `idInvoiceMilestone` non e'
   * presente su questo modello, solo sul DTO di salvataggio), quindi non c'e' nulla da
   * copiare dal padre a parte il default di `idTipologiaBlocco`.
   *
   * `parentSalKey` identifica il record SAL da cui e' stata invocata l'azione (tipicamente
   * il padre, ma qualsiasi riga della stessa milestone e' equivalente essendo il modello
   * flat): viene usato solo per verificare che la riga esista ancora nel gruppo prima di
   * appendere la nuova riga figlia (no-op silenzioso altrimenti).
   */
  const handleAddChild = (billingId: string, parentSalKey: number | string) => {
    setBillingPlan((prev) =>
      prev?.map((item) => {
        if (item.id !== billingId) {
          return item;
        }

        const parentExists = item.statoAvanzamentoLavoriList?.some((sal) => isSameSalRow(sal, parentSalKey));
        if (!parentExists) {
          return item;
        }

        const newChild: EditableStatoAvanzamentoLavori = {
          tempId: generateTempSalId(),
          isInviato: false,
          dataDiInvio: undefined,
          importo: undefined,
          idTipologiaBlocco: {
            value: 'LAVORAZIONI_IN_CORSO',
          },
        };

        return {
          ...item,
          statoAvanzamentoLavoriList: [...(item.statoAvanzamentoLavoriList ?? []), newChild],
        };
      })
    );
    setIsBillingDirty(true);
  };

  /**
   * Rimuove dallo stato locale (nessuna chiamata API/dispatch) la riga SAL identificata da
   * `salRowKey` nella milestone `billingId`, ma solo se non ha ancora un `id` reale
   * (bozza non salvata). Se la riga ha già un `id`, l'operazione è un no-op silenzioso con
   * solo un `console.warn` a scopo diagnostico: si è scelto di non lanciare eccezioni per
   * non rischiare di far crashare la UI nel caso il bottone elimina venga invocato in modo
   * inatteso (es. race condition con un salvataggio appena completato).
   */
  const handleRemoveChild = (billingId: string, salRowKey: number | string) => {
    setBillingPlan((prev) =>
      prev?.map((item) => {
        if (item.id !== billingId) {
          return item;
        }

        const target = item.statoAvanzamentoLavoriList?.find((sal) => isSameSalRow(sal, salRowKey));
        if (!target) {
          return item;
        }
        if (target.id !== undefined) {
          // eslint-disable-next-line no-console
          console.warn('handleRemoveChild: impossibile rimuovere una riga SAL già salvata (id definito).');
          return item;
        }

        return {
          ...item,
          statoAvanzamentoLavoriList: item.statoAvanzamentoLavoriList?.filter((sal) => !isSameSalRow(sal, salRowKey)),
        };
      })
    );
  };

  return {
    handleChangeCheckbox,
    handleChangeDate,
    handleSelectChange,
    handleChangeImporto,
    handleAddChild,
    handleRemoveChild,
  };
};

export default useEditBill;
